export interface PoseFeedbackCounts {
  feedback: number;
  success: number;
  undetected: number;
}

export type SessionFeedback = Record<string, PoseFeedbackCounts>;

const getPoseTotal = ({
  feedback,
  success,
  undetected,
}: PoseFeedbackCounts): number => feedback + success + undetected;

export const getPoseScoreFromFeedback = (
  poseFeedback: PoseFeedbackCounts,
): number => {
  const total = getPoseTotal(poseFeedback);

  if (total <= 0) {
    return 0;
  }

  return (poseFeedback.success / total) * 100;
};

export const getPoseErrorRateFromFeedback = (
  poseFeedback: PoseFeedbackCounts,
): number => {
  const total = getPoseTotal(poseFeedback);

  if (total <= 0) {
    return 0;
  }

  return poseFeedback.feedback / total;
};

export const isPoseMostlyUndetected = (
  poseFeedback: PoseFeedbackCounts,
): boolean => {
  const total = getPoseTotal(poseFeedback);

  return total > 0 && poseFeedback.undetected / total > 0.5;
};

const getScoredPoses = (sessionFeedback?: SessionFeedback | null) =>
  Object.entries(sessionFeedback ?? {})
    .filter(([, poseFeedback]) => getPoseTotal(poseFeedback) > 0)
    .map(([pose, poseFeedback]) => ({
      pose,
      score: getPoseScoreFromFeedback(poseFeedback),
    }));

export const getSessionScoreFromFeedback = (
  sessionFeedback?: SessionFeedback | null,
): number => {
  const scoredPoses = getScoredPoses(sessionFeedback);

  if (scoredPoses.length === 0) {
    return 0;
  }

  const averageScore =
    scoredPoses.reduce((total, pose) => total + pose.score, 0) /
    scoredPoses.length;

  return Math.floor(averageScore);
};

export const getPoseWithMostErrorsFromFeedback = (
  sessionFeedback?: SessionFeedback | null,
): string => {
  const poses = Object.entries(sessionFeedback ?? {}).filter(
    ([, poseFeedback]) => getPoseTotal(poseFeedback) > 0,
  );

  if (poses.length === 0) {
    return '';
  }

  const worstPose = poses.reduce((worst, current) =>
    getPoseErrorRateFromFeedback(current[1]) >
    getPoseErrorRateFromFeedback(worst[1])
      ? current
      : worst,
  );

  return getPoseErrorRateFromFeedback(worstPose[1]) > 0 ? worstPose[0] : '';
};

export const hasMostlyUndetectedPose = (
  sessionFeedback?: SessionFeedback | null,
): boolean => Object.values(sessionFeedback ?? {}).some(isPoseMostlyUndetected);
